import ToastMessage from "@/components/ToastMessage";
import Link from "next/link";
import { Image, ToastContainer } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faPhone } from '@fortawesome/free-solid-svg-icons';
import { useToasts } from "@/hooks/toasts";
import { useRouter } from "next/router";
import { useEffect, useState, useMemo } from "react";
import axios from "@/lib/axios";

export default function PublicLayout({ children })
{
  const { toasts, removeToast } = useToasts();
  const router = useRouter();
  const currentPath = router.pathname;
  const isActive = (path) => currentPath === path;
  const [customerTermsUrl, setCustomerTermsUrl] = useState("");
  const [customerPrivacyUrl, setCustomerPrivacyUrl] = useState("");
  const [resellerTermsUrl, setResellerTermsUrl] = useState("");
  const [resellerPrivacyUrl, setResellerPrivacyUrl] = useState("");
  const year = useMemo(() => new Date().getFullYear(), []);

  // Inject the widget script once and toggle its visibility via a body
  // data-attribute so the widget only appears on public pages.
  useEffect(() =>
  {
    const WIDGET_SRC = "https://instantpoll.multipleaisolutions.com/widget.js";
    const SCRIPT_ID = "instantpoll-widget-script";
    const STYLE_ID = "instantpoll-widget-style";

    // One-time: add a global CSS rule that hides the widget when the
    // data-attribute is absent (i.e. PublicLayout is not mounted).
    if (!document.getElementById(STYLE_ID))
    {
      const style = document.createElement("style");
      style.id = STYLE_ID;
      style.textContent =
        'body:not([data-instantpoll-active]) > elevenlabs-convai' +
        '{ display: none !important; }';
      document.head.appendChild(style);
    }

    // One-time: inject the widget script (never removed so it won't re-execute).
    if (!document.getElementById(SCRIPT_ID))
    {
      const script = document.createElement("script");
      script.src = WIDGET_SRC;
      script.id = SCRIPT_ID;
      script.async = true;
      document.body.appendChild(script);
    }

    // Show the widget while PublicLayout is mounted.
    document.body.setAttribute("data-instantpoll-active", "true");

    return () =>
    {
      // Hide the widget when PublicLayout unmounts.
      document.body.removeAttribute("data-instantpoll-active");
    };
  }, []);

  useEffect(() =>
  {
    const fetchPdfs = async () =>
    {
      const pdfEndpoints = [
        {
          key: "customerPrivacyUrl",
          setter: setCustomerPrivacyUrl,
          url: "/api/legal-documents/customer_privacy_policy",
          fallback: "/pdf/Customer_Privacy_Policy.pdf",
        },
        {
          key: "customerTermsUrl",
          setter: setCustomerTermsUrl,
          url: "/api/legal-documents/customer_terms_and_conditions",
          fallback: "/pdf/Customer_Terms_and_Conditions.pdf",
        },
        {
          key: "resellerPrivacyUrl",
          setter: setResellerPrivacyUrl,
          url: "/api/legal-documents/reseller_privacy_policy",
          fallback: "/pdf/Reseller_Privacy_Policy.pdf",
        },
        {
          key: "resellerTermsUrl",
          setter: setResellerTermsUrl,
          url: "/api/legal-documents/reseller_terms_and_conditions",
          fallback: "/pdf/Reseller_Terms_and_Conditions.pdf",
        },
      ];

      for (const doc of pdfEndpoints)
      {
        try
        {
          const res = await axios.get(doc.url);
          doc.setter(res?.data?.data?.url || doc.fallback);
        } catch
        {
          doc.setter(doc.fallback);
        }
      }
    };

    if (router.isReady) fetchPdfs();
  }, [router.isReady]);


  return (
    <>
      {/* HEADER */}
      <header>
        <nav className="navbar navbar-expand-lg navbar-dark custom-navbar">
          <div className="container nav-xl">
            <Link href="/" className="navbar-brand d-flex align-items-center">
              <Image
                src="/public/instant-polls-footer-logo.png"
                alt="Instant Polls Header Logo"
                className="footerLogo height40"
                loading="lazy"
              />
            </Link>

            <button
              className="navbar-toggler"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#navbarNav"
            >
              <span className="navbar-toggler-icon"></span>
            </button>

            <div className="collapse navbar-collapse justify-content-end" id="navbarNav">
              <ul className="navbar-nav align-items-lg-center">
                {[
                  { path: "/", label: "Home" },
                  { path: "/about", label: "About" },
                  { path: "/industry", label: "Industry" },
                  { path: "/reseller", label: "Reseller" },
                  { path: "/pricing", label: "Pricing" },
                  // { path: "/testdrive", label: "Watch Overview" },
                  { path: "/faq", label: "FAQ" },
                  { path: "/contact", label: "Contact" },
                ].map(({ path, label }) => (
                  <li key={path} className="nav-item">
                    <Link
                      href={path}
                      className={`nav-link ${isActive(path) ? "text-dark fw-bold" : "text-white"}`}
                    >
                      {label}
                    </Link>
                  </li>
                ))}

                <li className="nav-item">
                  <Link href="/login" className="nav-link text-light btn demo-btn">
                    Sign in
                  </Link>
                </li>
                <li className="nav-item">
                  <Link href="/register" className="nav-link text-light btn demo-btn">
                    Register
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </nav>
      </header>

      {/* MAIN */}
      <main className="public-content">
        {children}
        <ToastContainer position="top-end" className="p-3 z-index-top">
          {toasts?.map((toast, index) => (
            <ToastMessage
              key={index}
              onClose={() => removeToast(toast.id)}
              message={toast.message}
              variant={toast.variant}
            />
          ))}
        </ToastContainer>

      </main>


      {/* FOOTER */}
      <footer className="footer bg-dark text-white pt-5 pb-3 ">
        <div className="container responsiveZindex">
          <div className="row gy-4">
            {/* Company Info */}
            <div className="col-md-6">
              <div className="footer-brand d-flex align-items-center mb-3">
                <Link href="/">
                  <Image
                    src="/public/instant-polls-footer-logo.png"
                    className="footerLogo me-2"
                    alt="Instant Polls Footer Logo"
                    height="40"
                    loading="lazy"
                  />
                </Link>
              </div>
              <div className="footer-contact">
                <p>
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="contact-icon" />
                  Level 28/140 St Georges Terrace, Perth WA 6000, Australia
                </p>
                <p>
                  <FontAwesomeIcon icon={faPhone} className="contact-icon" />
                  <span className="pe-1">Phone Number: </span>   0468 097 414
                </p>

                <Link target="_blank" href='https://www.facebook.com/Instant.p0lls/'>  <i className="bi bi-facebook socialMediaIcons"></i> </Link>
                <Link target="_blank" href='https://www.linkedin.com/company/instant-polls'> <i className="bi bi-linkedin socialMediaIcons"></i></Link>
                <Link target="_blank" href='https://www.youtube.com/@InstantPolls'> <i className="bi bi-youtube socialMediaIcons  "></i></Link>
              </div>
            </div>

            {/* Navigation Columns */}
            <div className="col-6 col-md-2">
              <ul className="list-unstyled footer-links">
                <li><Link href="/">Home</Link></li>
                <li><Link href="/">Book a Demo</Link></li>
                <li><Link href="/about">About</Link></li>
                <li><Link href="/industry">Industry</Link></li>
              </ul>
            </div>

            <div className="col-6 col-md-2">
              <ul className="list-unstyled footer-links">
                <li><Link href="/reseller">Reseller</Link></li>
                <li><Link href="/pricing">Pricing</Link></li>
                {/* <li><Link href="/testdrive">Watch Overview</Link></li> */}
                <li><Link href="/faq">FAQ</Link></li>
              </ul>
            </div>

            <div className="col-6 col-md-2">
              <ul className="list-unstyled footer-links">
                <li><Link href="/contact">Contact</Link></li>
                <li><Link href="/login">Sign In</Link></li>
                <li><Link href="/register">Register</Link></li>
              </ul>
            </div>
          </div>

          <hr className="border-light mt-4 mb-3" />
          {/* Bottom Footer */}
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start text-center text-sm-start">
            <p className="mb-0 text-white small mt-3">
              © {year} Instant Polls. All Rights Reserved.
            </p>

            <div className="d-flex flex-column flex-sm-row text-sm-end mt-3 mt-sm-0">
              <div className="me-sm-4 mb-2 mb-sm-0">
                <h6 className="footer-sub-heading text-start secondaryColor font16">Resellers</h6>
                <div className="terms-policy text-start">
                  <a href={resellerTermsUrl} target="_blank" rel="noopener noreferrer">
                    Terms & Conditions
                  </a>
                  <a href={customerPrivacyUrl} target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                </div>
              </div>
              <div className="me-sm-4 mb-2 mb-sm-0" style={{ zIndex: 10 }}>
                <h6 className="footer-sub-heading text-start secondaryColor font16">Customer</h6>
                <div className="terms-policy text-start">
                  <a href={customerTermsUrl} target="_blank" rel="noopener noreferrer">
                    Terms & Conditions
                  </a>
                  <a href={customerPrivacyUrl} target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}